/** Shared PocketBase model types for the Aria Robotics admin + public site. */

export interface PbRecord {
  id: string;
  collectionId: string;
  collectionName: string;
  created: string;
  updated: string;
}

// --- existing shared `users` auth collection (not modified by us) ---
export type UserRole = 'user' | 'superuser' | 'admin' | 'moderator';

export interface AppUser extends PbRecord {
  email: string;
  emailVisibility: boolean;
  verified: boolean;
  name: string;
  role: UserRole;
  banned: boolean;
  username: string;
  avatar: string;
  bio: string;
}

/** Roles allowed into the admin area. `admin` = full; `moderator` = editor. */
export const STAFF_ROLES: readonly UserRole[] = ['admin', 'moderator', 'superuser'];

export function isStaff(user: Pick<AppUser, 'role' | 'banned'> | null): boolean {
  if (!user) return false;
  if (user.banned) return false;
  return STAFF_ROLES.includes(user.role);
}

export function isAdmin(user: Pick<AppUser, 'role' | 'banned'> | null): boolean {
  if (!user || user.banned) return false;
  return user.role === 'admin' || user.role === 'superuser';
}

// --- aria_* collections (created by scripts/provision-pb.mjs) ---
export interface Category extends PbRecord {
  name: string;
  slug: string;
}

export type PostStatus = 'draft' | 'published';

export interface Post extends PbRecord {
  title: string;
  slug: string;
  summary: string;
  content: string; // sanitized HTML from the editor
  image: string; // stored filename ('' when none)
  seo_title: string;
  seo_description: string;
  seo_keyword: string;
  category: string; // relation id ('' when none)
  status: PostStatus;
  author: string; // relation id
  published_at: string; // ISO date or ''
  expand?: {
    category?: Category;
    author?: AppUser;
  };
}

export interface Settings extends PbRecord {
  contact_recipient: string;
  contact_subject_prefix: string;
  notifications_enabled: boolean;
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_pass: string;
  smtp_tls: boolean;
  smtp_from_email: string;
  smtp_from_name: string;
}

export interface ContactMessage extends PbRecord {
  name: string;
  email: string;
  subject: string;
  message: string;
}

/** Collection name constants — single source of truth. */
export const COLLECTIONS = {
  users: 'users',
  categories: 'aria_categories',
  posts: 'aria_posts',
  settings: 'aria_settings',
  contacts: 'aria_contacts',
} as const;
